import {
    todayGroupHeader,
    yesterdayGroupHeader,
    thisWeekGroupHeader,
    lastWeekGroupHeader,
    twoWeeksAgoGroupHeader,
    threeWeeksAgoGroupHeader,
    thisMonthGroupHeader,
    lastMonthGroupHeader,
    olderGroupHeader,
} from './generateTimeGroupHeaders.locstring.json';
import { pinnedGroupHeader } from 'owa-locstrings/lib/strings/pinnedgroupheader.locstring.json';
import { decemberUppercase } from 'owa-locstrings/lib/strings/decemberuppercase.locstring.json';
import { novemberUppercase } from 'owa-locstrings/lib/strings/novemberuppercase.locstring.json';
import { octoberUppercase } from 'owa-locstrings/lib/strings/octoberuppercase.locstring.json';
import { septemberUppercase } from 'owa-locstrings/lib/strings/septemberuppercase.locstring.json';
import { augustUppercase } from 'owa-locstrings/lib/strings/augustuppercase.locstring.json';
import { julyUppercase } from 'owa-locstrings/lib/strings/julyuppercase.locstring.json';
import { juneUppercase } from 'owa-locstrings/lib/strings/juneuppercase.locstring.json';
import { mayUppercase } from 'owa-locstrings/lib/strings/mayuppercase.locstring.json';
import { aprilUppercase } from 'owa-locstrings/lib/strings/apriluppercase.locstring.json';
import { marchUppercase } from 'owa-locstrings/lib/strings/marchuppercase.locstring.json';
import { februaryUppercase } from 'owa-locstrings/lib/strings/februaryuppercase.locstring.json';
import { januaryUppercase } from 'owa-locstrings/lib/strings/januaryuppercase.locstring.json';
import { calendarDayOfWeek_Sunday } from 'owa-locstrings/lib/strings/calendardayofweek_sunday.locstring.json';
import { calendarDayOfWeek_Monday } from 'owa-locstrings/lib/strings/calendardayofweek_monday.locstring.json';
import { calendarDayOfWeek_Tuesday } from 'owa-locstrings/lib/strings/calendardayofweek_tuesday.locstring.json';
import { calendarDayOfWeek_Wednesday } from 'owa-locstrings/lib/strings/calendardayofweek_wednesday.locstring.json';
import { calendarDayOfWeek_Thursday } from 'owa-locstrings/lib/strings/calendardayofweek_thursday.locstring.json';
import { calendarDayOfWeek_Friday } from 'owa-locstrings/lib/strings/calendardayofweek_friday.locstring.json';
import { calendarDayOfWeek_Saturday } from 'owa-locstrings/lib/strings/calendardayofweek_saturday.locstring.json';
import loc from 'owa-localize';
import type TimeGroupHeader from '../type/TimeGroupHeader';
import { type TimeGroupHeaderType } from '../type/TimeGroupHeader';
import { TimeHeaderId } from '../type/GroupHeaderId';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isWin32StylingEnabled } from 'owa-theme-win32-style-store';
import type { OwaDate } from 'owa-datetime';
import {
    addDays,
    addSeconds,
    differenceInCalendarDays,
    getMonth,
    getYear,
    MIN_OUTLOOK_DATE,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears,
    utcDate,
} from 'owa-datetime';

// References in time that are used to build time group headers.
interface TimeGroupDateTimeReferences {
    adjustedTime: OwaDate;
    currentYear: number;
    lastWeek: OwaDate;
    oneMonthAgo: OwaDate;
    pinnedItemTimestamp: OwaDate;
    startofThisMonth: OwaDate;
    startOfThisWeek: OwaDate;
    threeWeeksAgo: OwaDate;
    todayStartTime: OwaDate;
    tomorrowStartTime: OwaDate;
    twoMonthsAgo: OwaDate;
    twoWeeksAgo: OwaDate;
    weekStartDay: number;
    yesterdayStartTime: OwaDate;
}

/**
 * Generate time group headers
 * @param time Time specified in user time zone based on which to generate time ranges
 * @return An array of time group ranges for mail list headers
 */
export default function generateTimeGroupHeaders(
    time: OwaDate,
    weekStartDay: number
): TimeGroupHeader[] {
    const monthNames: string[] = [
        loc(januaryUppercase),
        loc(februaryUppercase),
        loc(marchUppercase),
        loc(aprilUppercase),
        loc(mayUppercase),
        loc(juneUppercase),
        loc(julyUppercase),
        loc(augustUppercase),
        loc(septemberUppercase),
        loc(octoberUppercase),
        loc(novemberUppercase),
        loc(decemberUppercase),
    ];

    // Weekday names, indexed to match the 0 (Sunday) - 6 (Saturday) convention used by
    // WeekStartDay and OwaDate week calculations.
    const weekdayNames: string[] = [
        loc(calendarDayOfWeek_Sunday),
        loc(calendarDayOfWeek_Monday),
        loc(calendarDayOfWeek_Tuesday),
        loc(calendarDayOfWeek_Wednesday),
        loc(calendarDayOfWeek_Thursday),
        loc(calendarDayOfWeek_Friday),
        loc(calendarDayOfWeek_Saturday),
    ];

    // References in time that are used to build time group headers.
    const dateTimeReferences: TimeGroupDateTimeReferences = {
        adjustedTime: time,
        currentYear: getYear(time),
        lastWeek: startOfWeek(subWeeks(time, 1), weekStartDay),
        oneMonthAgo: startOfMonth(subMonths(time, 1)),
        pinnedItemTimestamp: utcDate(4500, 8, 1) /* Sept 1 4500 */,
        startofThisMonth: startOfMonth(time),
        startOfThisWeek: startOfWeek(time, weekStartDay),
        threeWeeksAgo: startOfWeek(subWeeks(time, 3), weekStartDay),
        todayStartTime: startOfDay(time),
        tomorrowStartTime: startOfDay(addDays(time, 1)),
        twoMonthsAgo: startOfMonth(subMonths(time, 2)),
        twoWeeksAgo: startOfWeek(subWeeks(time, 2), weekStartDay),
        weekStartDay,
        yesterdayStartTime: startOfDay(subDays(time, 1)),
    };

    const consumerHeaders = [0, 1, 2, 5, 10, 11, 9];

    const enterpriseHeaders = [0, 1, 2, 3, 4, 5, 6, 7, 8];

    // Header types matching Win32 (classic) Outlook's more granular grouping,
    // used when the Win32 style setting is enabled: Today, Yesterday, individual
    // weekday names for the rest of the current week, Last week, Two/Three weeks
    // ago, Last month, and a single Older bucket for everything before that.
    const win32Headers = [0, 1, 2, 14, 4, 12, 13, 16, 15];

    // Determine header types depending on the product.
    const headerTypes = isWin32StylingEnabled()
        ? win32Headers
        : isConsumer()
        ? consumerHeaders
        : enterpriseHeaders;

    // Generate headers from header types.
    return generateTimeGroupHeadersFromTimeGroupHeaderTypes(
        dateTimeReferences,
        headerTypes,
        monthNames,
        weekdayNames
    );
}

/**
 * Generates headers from header types.
 * @param dateTimeReferences Object with references required to build TimeGroupHeader objects
 * @param headerTypes Determines which headers are returned
 * @param monthNames Array of month name strings
 * @param weekdayNames Array of weekday name strings, indexed 0 (Sunday) - 6 (Saturday)
 * @returns An array of header objects
 */
function generateTimeGroupHeadersFromTimeGroupHeaderTypes(
    dateTimeReferences: TimeGroupDateTimeReferences,
    headerTypes: TimeGroupHeaderType[],
    monthNames: string[],
    weekdayNames: string[]
): TimeGroupHeader[] {
    const timeGroupHeaders: TimeGroupHeader[] = [];

    for (const headerType of headerTypes) {
        const timeGroupHeadersFromHeaderType = mapHeaderTypeToTimeGroupHeaders(
            dateTimeReferences,
            headerType,
            monthNames,
            weekdayNames
        );

        timeGroupHeaders.push(...timeGroupHeadersFromHeaderType);
    }

    return timeGroupHeaders;
}

/**
 * Maps header type to header objects.
 * @param dateTimeReferences Object with references required to build TimeGroupHeader objects
 * @param headerType Determines which headers are returned
 * @param monthNames Array of month name strings
 * @param weekdayNames Array of weekday name strings, indexed 0 (Sunday) - 6 (Saturday)
 * @returns An array of header objects
 */
function mapHeaderTypeToTimeGroupHeaders(
    dateTimeReferences: TimeGroupDateTimeReferences,
    headerType: TimeGroupHeaderType,
    monthNames: string[],
    weekdayNames: string[]
): TimeGroupHeader[] {
    switch (headerType) {
        // "Pinned" header.
        case 0:
            return [
                {
                    headerText: () => loc(pinnedGroupHeader),
                    headerId: TimeHeaderId.Pinned,
                    rangeStartTime: dateTimeReferences.pinnedItemTimestamp,
                    rangeEndTime: addSeconds(
                        dateTimeReferences.pinnedItemTimestamp,
                        1
                    ) /* add one second */,
                },
            ];
        // "Today" header.
        case 1:
            return [
                {
                    headerText: () => loc(todayGroupHeader),
                    headerId: TimeHeaderId.Today,
                    rangeStartTime: dateTimeReferences.todayStartTime,
                    rangeEndTime: dateTimeReferences.tomorrowStartTime,
                },
            ];
        // "Yesterday" header.
        case 2:
            return [
                {
                    headerText: () => loc(yesterdayGroupHeader),
                    headerId: TimeHeaderId.Yesterday,
                    rangeStartTime: dateTimeReferences.yesterdayStartTime,
                    rangeEndTime: dateTimeReferences.todayStartTime,
                },
            ];
        // "This week" header.
        case 3:
            return [
                {
                    headerText: () => loc(thisWeekGroupHeader),
                    headerId: TimeHeaderId.ThisWeek,
                    rangeStartTime: dateTimeReferences.startOfThisWeek,
                    rangeEndTime: dateTimeReferences.yesterdayStartTime,
                },
            ];
        // "Last week" header.
        case 4:
            return [
                {
                    headerText: () => loc(lastWeekGroupHeader),
                    headerId: TimeHeaderId.LastWeek,
                    rangeStartTime: dateTimeReferences.lastWeek,
                    rangeEndTime: dateTimeReferences.startOfThisWeek,
                },
            ];
        // "Two weeks ago" header (Win32 grouping only).
        case 12:
            return [
                {
                    headerText: () => loc(twoWeeksAgoGroupHeader),
                    headerId: TimeHeaderId.TwoWeeksAgo,
                    rangeStartTime: dateTimeReferences.twoWeeksAgo,
                    rangeEndTime: dateTimeReferences.lastWeek,
                },
            ];
        // "Three weeks ago" header (Win32 grouping only).
        case 13:
            return [
                {
                    headerText: () => loc(threeWeeksAgoGroupHeader),
                    headerId: TimeHeaderId.ThreeWeeksAgo,
                    rangeStartTime: dateTimeReferences.threeWeeksAgo,
                    rangeEndTime: dateTimeReferences.twoWeeksAgo,
                },
            ];
        // Individual weekday name headers (e.g. "Monday") for the remaining days of the
        // current week that aren't already covered by "Today"/"Yesterday" (Win32 grouping only).
        case 14: {
            const weekdayHeaders: TimeGroupHeader[] = [];

            let weekdayHeaderStartTime = subDays(dateTimeReferences.yesterdayStartTime, 1);

            while (weekdayHeaderStartTime >= dateTimeReferences.startOfThisWeek) {
                const dayOfWeek =
                    (dateTimeReferences.weekStartDay +
                        differenceInCalendarDays(
                            weekdayHeaderStartTime,
                            dateTimeReferences.startOfThisWeek
                        )) %
                    7;
                const weekdayName = weekdayNames[dayOfWeek];
                const weekdayHeaderStart = weekdayHeaderStartTime;

                weekdayHeaders.push({
                    headerText: () => weekdayName,
                    headerId: weekdayName,
                    rangeStartTime: weekdayHeaderStart,
                    rangeEndTime: addDays(weekdayHeaderStart, 1),
                });

                weekdayHeaderStartTime = subDays(weekdayHeaderStartTime, 1);
            }

            return weekdayHeaders;
        }
        // "This month" header.
        case 5:
            if (dateTimeReferences.startofThisMonth < dateTimeReferences.yesterdayStartTime) {
                return [
                    {
                        headerText: () => loc(thisMonthGroupHeader),
                        headerId: TimeHeaderId.ThisMonth,
                        rangeStartTime: dateTimeReferences.startofThisMonth,
                        rangeEndTime: dateTimeReferences.yesterdayStartTime,
                    },
                ];
            }

            return [];
        // "Last month" header.
        case 6:
            return [
                {
                    headerText: () => loc(lastMonthGroupHeader),
                    headerId: TimeHeaderId.LastMonth,
                    rangeStartTime: dateTimeReferences.oneMonthAgo,
                    rangeEndTime: dateTimeReferences.startofThisMonth,
                },
            ];
        // "Last month" header for Win32 grouping. Ends at "Three weeks ago" (not the calendar
        // month start) so the Win32 ranges stay contiguous with no gaps or overlaps.
        case 16:
            return [
                {
                    headerText: () => loc(lastMonthGroupHeader),
                    headerId: TimeHeaderId.LastMonth,
                    rangeStartTime: dateTimeReferences.oneMonthAgo,
                    rangeEndTime: dateTimeReferences.threeWeeksAgo,
                },
            ];
        // Haders for specific months of the year, up until the previous year.
        case 7:
            /**
             * Add headers for preceding months of the current year (not including the
             * previous month, as that is handled by "Last month" header).
             */
            const monthHeaders: TimeGroupHeader[] = [];

            let monthIterator = 2;
            let monthHeaderStartTime = startOfMonth(
                subMonths(dateTimeReferences.adjustedTime, monthIterator)
            );
            let monthHeaderEndTime = startOfMonth(
                subMonths(dateTimeReferences.adjustedTime, monthIterator - 1)
            );

            while (getYear(monthHeaderStartTime) === dateTimeReferences.currentYear) {
                const monthName = monthNames[getMonth(monthHeaderStartTime)];

                monthHeaders.push({
                    headerText: () => monthName,
                    headerId: monthName,
                    rangeStartTime: monthHeaderStartTime,
                    rangeEndTime: monthHeaderEndTime,
                });

                monthIterator++;
                monthHeaderStartTime = startOfMonth(
                    subMonths(dateTimeReferences.adjustedTime, monthIterator)
                );
                monthHeaderEndTime = startOfMonth(
                    subMonths(dateTimeReferences.adjustedTime, monthIterator - 1)
                );
            }

            return monthHeaders;
        // Headers for specific years (before current year and until 1970).
        case 8:
            /**
             * Add headers for preceding years until 1970 (not including the current
             * year, as that is handled by the month headers)
             */
            const yearHeaders: TimeGroupHeader[] = [];

            let yearIterator = 1;
            let yearStartTime = startOfYear(
                subYears(dateTimeReferences.adjustedTime, yearIterator)
            );
            let yearEndTime = startOfYear(
                subYears(dateTimeReferences.adjustedTime, yearIterator - 1)
            );

            const earliestYear = 1970; // corresponds to Unix/JavaScript epoch.
            while (getYear(yearStartTime) >= earliestYear) {
                const year = getYear(yearStartTime);

                yearHeaders.push({
                    headerText: () => year.toString(),
                    headerId: year,
                    rangeStartTime: yearStartTime,
                    rangeEndTime: yearEndTime,
                });

                yearIterator++;
                yearStartTime = startOfYear(
                    subYears(dateTimeReferences.adjustedTime, yearIterator)
                );
                yearEndTime = startOfYear(
                    subYears(dateTimeReferences.adjustedTime, yearIterator - 1)
                );
            }

            return yearHeaders;
        // "Older" header.
        case 9:
            return [
                {
                    headerText: () => loc(olderGroupHeader),
                    headerId: TimeHeaderId.Older,
                    rangeStartTime: MIN_OUTLOOK_DATE /* Jan 1st 1601 00:00:00 UTC */,
                    rangeEndTime: dateTimeReferences.twoMonthsAgo,
                },
            ];
        // "Older" header covering everything before "Last month" (Win32 grouping only,
        // which doesn't break "older" mail down further by month/year).
        case 15:
            return [
                {
                    headerText: () => loc(olderGroupHeader),
                    headerId: TimeHeaderId.Older,
                    rangeStartTime: MIN_OUTLOOK_DATE /* Jan 1st 1601 00:00:00 UTC */,
                    rangeEndTime: dateTimeReferences.oneMonthAgo,
                },
            ];
        // Header for one month ago.
        case 10:
            return [
                {
                    headerText: () => monthNames[getMonth(dateTimeReferences.oneMonthAgo)],
                    headerId: TimeHeaderId.OneMonthOlder,
                    rangeStartTime: dateTimeReferences.oneMonthAgo,
                    rangeEndTime: dateTimeReferences.startofThisMonth,
                },
            ];
        // Header for two months ago.
        case 11:
            return [
                {
                    headerText: () => monthNames[getMonth(dateTimeReferences.twoMonthsAgo)],
                    headerId: TimeHeaderId.TwoMonthsOlder,
                    rangeStartTime: dateTimeReferences.twoMonthsAgo,
                    rangeEndTime: dateTimeReferences.oneMonthAgo,
                },
            ];
        default:
            throw new Error(
                'Invalid TimeGroupHeaderType passed to mapHeaderTypeToTimeGroupHeaders'
            );
    }
}
